import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Button, CircularProgress } from '@mui/material';
import { DashboardContainer } from '../../Containers/DashboardLayout';
import { Toasts } from '../../Components/Toasts/Toasts';
import {
  blockAnonymousUser,
  clearCursorCache,
  deactivateAnonymousUser,
  fetchAnonymousUsers,
  getCountOfAnonymousUsers,
  unblockAnonymousUser,
} from './saga/saga';

const PAGE_SIZE = 25;

export const AnonymousUsers = () => {
  const [page, setPage] = useState(0);
  const [list, setList] = useState([]);
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const items = await fetchAnonymousUsers({ page: p, docLimit: PAGE_SIZE });
      setList((prev) => (p === 0 ? items : [...prev, ...items]));
      if (items.length < PAGE_SIZE) setExhausted(true);
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(0);
    getCountOfAnonymousUsers().then(setCount).catch(() => null);
    // Drop the module-level cursor cache on unmount — otherwise switching
    // to Users / Posts and coming back replays the same cursor against a
    // freshly-mounted list with reset state, yielding stale rows.
    return () => clearCursorCache();
  }, [load]);

  const onLoadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const onBlock = async (id) => {
    try {
      await blockAnonymousUser({ userId: id });
      setList((prev) =>
        prev.map((u) => (u.id === id ? { ...u, isBlocked: true } : u)),
      );
      Toasts.success('Anon session blocked');
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'block failed');
    }
  };

  const onUnblock = async (id) => {
    try {
      await unblockAnonymousUser({ userId: id });
      setList((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, isBlocked: false, isDeactivated: false } : u,
        ),
      );
      Toasts.success('Anon session unblocked');
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'unblock failed');
    }
  };

  const onDeactivate = async (id) => {
    try {
      await deactivateAnonymousUser({ userId: id });
      setList((prev) =>
        prev.map((u) => (u.id === id ? { ...u, isDeactivated: true } : u)),
      );
      Toasts.success('Anon session deactivated');
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'deactivate failed');
    }
  };

  return (
    <DashboardContainer>
      <div className="p-4">
        <h3 className="text-[22px] font-bold">
          Anonymous {count !== null && count > 0 && ` - ${count}`}
        </h3>
        <p className="text-[13px] text-gray-500 mb-3">
          Sessions that haven't completed signin yet. Most are real visitors
          who never converted; block here if you spot abuse from a specific
          row (e.g. posting spam from an anon session).
        </p>

        <div className="mt-2 bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-left text-[14px]" style={{ minWidth: 720 }}>
            <thead className="bg-gray-50 text-gray-600 text-[12px] uppercase">
              <tr>
                <th className="px-3 py-2 whitespace-nowrap">id</th>
                <th className="px-3 py-2 whitespace-nowrap">created</th>
                <th className="px-3 py-2 whitespace-nowrap">last seen</th>
                <th className="px-3 py-2 whitespace-nowrap">status</th>
                <th className="px-3 py-2 whitespace-nowrap">action</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && !loading ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    No anonymous sessions.
                  </td>
                </tr>
              ) : (
                list.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[12px]">
                      {u.id.slice(0, 8)}…
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {moment(u.createdAt).fromNow()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {u.lastSeenAt ? moment(u.lastSeenAt).fromNow() : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {u.isBlocked ? (
                        <span className="text-red-600 font-semibold">blocked</span>
                      ) : u.isDeactivated ? (
                        <span className="text-gray-500">deactivated</span>
                      ) : (
                        <span className="text-green-700">active</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex gap-1">
                        {(u.isBlocked || u.isDeactivated) ? (
                          <Button size="small" onClick={() => onUnblock(u.id)}>
                            Unblock
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => onBlock(u.id)}
                            >
                              Block
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              onClick={() => onDeactivate(u.id)}
                            >
                              Deactivate
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center mt-4">
          {loading ? (
            <CircularProgress size={20} />
          ) : exhausted ? (
            <span className="text-gray-400 text-[13px]">No more rows.</span>
          ) : (
            <Button onClick={onLoadMore} variant="outlined" size="small">
              Load more
            </Button>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};
